export type ProjectDetail = {
  client: string
  location: string
  year: string
  services: string[]
  challenge: string
  approach: string
  outcome: string
  stats: { value: string; label: string }[]
}

// Case-study content per project (placeholder copy — replace with real case studies)
export const projectDetails: Record<string, ProjectDetail> = {
  'Maison Obsidian': {
    client: 'Maison Obsidian',
    location: 'Paris & Dubai',
    year: '2026',
    services: ['Brand ID', 'Cinematic film', 'Private terminal'],
    challenge:
      'A new haute parfumerie house needed to enter a market defined by centuries-old names — with one collection, no heritage and a launch window of eight weeks.',
    approach:
      'We built the house as a system: a restrained identity cut from a single faceted form, a cinematic film shot in near-darkness, and a private terminal where collectors reserve bottles before release. Production ran on our AI-OS, so every frame and page stayed handcrafted while the pace stayed fast.',
    outcome:
      'The first collection sold out to private clients before public release. LAEBEL Ventures holds a stake in the house and continues to operate its brand environment.',
    stats: [
      { value: '8 weeks', label: 'From brief to launch' },
      { value: '100%', label: 'Pre-release allocation' },
      { value: 'Equity', label: 'LAEBEL Ventures partner' },
    ],
  },
  'Halcyon Bio': {
    client: 'Halcyon Bio',
    location: 'Zurich',
    year: '2025',
    services: ['Brand ID', 'Visual intelligence', 'Investor terminal'],
    challenge:
      'A neuro-performance and longevity biotech had strong science and no visual language that investors or clinicians could trust at first glance.',
    approach:
      'We translated their research into a calm, clinical identity and a visual intelligence system — data views, study summaries and product imagery built from one set of rules, rendered with algorithmic precision.',
    outcome:
      'The new identity and terminal anchored their Series A materials and became the shared reference for every clinic partner.',
    stats: [
      { value: '6 weeks', label: 'Identity to live terminal' },
      { value: '1 system', label: 'For research, product and investors' },
      { value: '3 markets', label: 'Clinic partners onboarded' },
    ],
  },
  'Sable Estates': {
    client: 'Sable Estates',
    location: 'Mallorca & Dubai',
    year: '2025',
    services: ['Data room', 'Investor terminal', 'Asset visuals'],
    challenge:
      'An off-market villa portfolio was sold through PDFs and private calls. Investors wanted confidence and discretion; the fund wanted speed.',
    approach:
      'We designed a private data room and investor terminal: invitation-only access, build-phase tracking, and quiet editorial visuals for every asset — no listings, no noise.',
    outcome:
      'Investors now review every asset in one place, and the fund closes allocations without a single public listing.',
    stats: [
      { value: '4 weeks', label: 'To first investor access' },
      { value: '0', label: 'Public listings needed' },
      { value: '2 regions', label: 'In one terminal' },
    ],
  },
  'Meridian Office': {
    client: 'Meridian Office',
    location: 'Dubai',
    year: '2026',
    services: ['AI-OS', 'Agents', 'RAG memory'],
    challenge:
      'A single family office held decades of decisions in emails, folders and the heads of a few people. Every new investment started from scratch.',
    approach:
      'LAEBEL Labs deployed a private AI-OS: protected vector memory of the office’s history, agents that prepare investment briefs, and automated workflows for reporting — all running inside their own infrastructure.',
    outcome:
      'Investment memos that took a week now take an afternoon, and institutional knowledge no longer depends on who is in the room.',
    stats: [
      { value: '90%', label: 'Less time per memo' },
      { value: '20 years', label: 'Of decisions indexed' },
      { value: 'Private', label: 'Runs on own infrastructure' },
    ],
  },
  'Aurel Eyewear': {
    client: 'Aurel Eyewear',
    location: 'Milan',
    year: '2026',
    services: ['Brand ID', 'Campaign film', 'Launch'],
    challenge:
      'An independent eyewear house made frames by hand in tiny runs. Their brand looked like everyone else’s — glossy, loud and forgettable.',
    approach:
      'We stripped everything back to the object: a reduced identity, a campaign film lit by a single source, and a launch built around a waiting list rather than a sale.',
    outcome:
      'The first edition sold through the waiting list in two days, and the film runs across SuperLuxury’s channels.',
    stats: [
      { value: '2 days', label: 'Edition sold through' },
      { value: '1.2M+', label: 'Reach via SuperLuxury' },
      { value: '5 weeks', label: 'From brief to film' },
    ],
  },
  'Orbit Aero': {
    client: 'Orbit Aero',
    location: 'Dubai & Geneva',
    year: '2025',
    services: ['Brand ID', 'Cinematic film', 'Equity'],
    challenge:
      'A private aviation operator on the Gulf–Europe corridor needed to look like the calmest choice in a market full of glossy promises.',
    approach:
      'We built an identity around stillness — wide horizons, empty runways, quiet type — and a film shot at dawn. LAEBEL Ventures joined as a partner, trading brand building for equity.',
    outcome:
      'Orbit Aero now books most flights through returning clients and referrals, and the brand is an asset on the balance sheet.',
    stats: [
      { value: 'Equity', label: 'LAEBEL Ventures partner' },
      { value: '2 hubs', label: 'Dubai and Geneva' },
      { value: '7 weeks', label: 'Identity and film' },
    ],
  },
  Solace: {
    client: 'Solace',
    location: 'Mallorca',
    year: '2025',
    services: ['Spaces', 'Brand ID', 'Longevity platform'],
    challenge:
      'Longevity and neuro-performance residences in Mallorca needed a brand that felt like medicine and like rest at the same time.',
    approach:
      'We shaped the identity, the treatment rooms and the member platform as one environment: limestone, still water and a booking experience with no friction.',
    outcome:
      'Founding memberships were fully allocated before the residences opened.',
    stats: [
      { value: 'Sold out', label: 'Founding memberships' },
      { value: '1 source', label: 'Space, brand and platform' },
      { value: '12 weeks', label: 'Concept to opening assets' },
    ],
  },
  'Maison Dune': {
    client: 'Maison Dune',
    location: 'Abu Dhabi',
    year: '2024',
    services: ['Spaces', 'Terminal', 'Equity'],
    challenge:
      'A desert hospitality asset needed a brand, a booking terminal and an investment story — for guests and owners alike.',
    approach:
      'We designed the guest experience and the owner terminal from the same source, and held a stake through LAEBEL Ventures from day one.',
    outcome:
      'The property opened with a waiting list and reports to owners through a single live terminal.',
    stats: [
      { value: 'Equity', label: 'Held via LAEBEL Ventures' },
      { value: '1 terminal', label: 'For guests and owners' },
      { value: 'Waitlist', label: 'At opening' },
    ],
  },
  'Atelier Vauclair': {
    client: 'Atelier Vauclair',
    location: 'Vallée de Joux',
    year: '2024',
    services: ['Brand ID', 'Cinematic film', 'Campaign'],
    challenge:
      'An independent watch manufacture with extraordinary movements and an identity that had never been designed.',
    approach:
      'We built a mark from the geometry of their bridges, filmed the movements in macro, and launched the campaign to collectors first.',
    outcome:
      'Collector allocations for the next two years were reserved within a month of launch.',
    stats: [
      { value: '2 years', label: 'Allocations reserved' },
      { value: 'Macro film', label: 'Shot in-house' },
      { value: '6 weeks', label: 'Brief to campaign' },
    ],
  },
  'Nowa Residences': {
    client: 'Nowa Residences',
    location: 'Warsaw',
    year: '2025',
    services: ['Sales terminal', 'Build-phase tracking', 'Visuals'],
    challenge:
      'A Warsaw development needed to sell to DACH investors before construction began — without visiting the site.',
    approach:
      'We built a sales terminal with live build-phase tracking and editorial visuals that show the building as it will feel, not just how it will look.',
    outcome:
      'The terminal went live before the first floor was poured, and most units were reserved remotely.',
    stats: [
      { value: 'Pre-build', label: 'Terminal live' },
      { value: 'Remote', label: 'Most reservations' },
      { value: '3 weeks', label: 'To launch' },
    ],
  },
  'Port Hercule': {
    client: 'Port Hercule',
    location: 'Monaco',
    year: '2024',
    services: ['Cinematic film', 'Distribution', 'SuperLuxury'],
    challenge:
      'A superyacht series needed to reach a small, global audience that ignores advertising.',
    approach:
      'We produced a cinematic series and distributed it through SuperLuxury — editorial first, never an ad.',
    outcome:
      'The series reached its audience organically and led to private viewings in Monaco.',
    stats: [
      { value: '1.2M+', label: 'Audience via SuperLuxury' },
      { value: '4 films', label: 'In the series' },
      { value: 'Private', label: 'Viewings arranged' },
    ],
  },
  'Lindt & Aare': {
    client: 'Lindt & Aare',
    location: 'Zurich',
    year: '2025',
    services: ['Brand ID', 'Client terminal', 'AI-OS'],
    challenge:
      'A private bank needed a digital presence that felt as discreet as its offices — and tools that made its advisers faster.',
    approach:
      'We designed a reserved identity and a client terminal, and connected advisers to an AI-OS that prepares client briefings from protected memory.',
    outcome:
      'Clients reach their portfolio in one quiet place, and advisers prepare meetings in minutes.',
    stats: [
      { value: 'Minutes', label: 'To prepare a briefing' },
      { value: '1 terminal', label: 'For every client' },
      { value: 'Swiss', label: 'Hosted and private' },
    ],
  },
}
